import { useState, useEffect } from 'react'
import { supabase } from './supabase'

const STATUS_COLOR = {
  active: '#00563F', pending: '#C19A6B', claimed: '#C19A6B',
  settled: '#7A7570', cancelled: '#7A7570', disputed: '#C0392B', declined: '#7A7570', voided: '#C0392B', withdrawn: '#7A7570'
}
const STATUS_LABEL = {
  active: 'ACTIVE', pending: 'PENDING', claimed: 'CLAIMED',
  settled: 'SETTLED', cancelled: 'CANCELLED', disputed: 'DISPUTED', declined: 'DECLINED', voided: 'VOIDED — No Agreement', withdrawn: 'WITHDRAWN'
}

export default function BettingLedger({ session }) {
  const [entries, setEntries] = useState([])
  const [profiles, setProfiles] = useState({})
  const [matches, setMatches] = useState({})
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')

  useEffect(() => { load() }, [session])

  const load = async () => {
    setLoading(true)

    // Load all table games
    const { data: myTablePlayers } = await supabase
      .from('crossbet_table_players')
      .select('table_id, total_paid, status')
      .eq('player_id', session.user.id)
    const tableIds = [...new Set((myTablePlayers || []).map(p => p.table_id))]
    let tableEntries = []
    if (tableIds.length > 0) {
      const { data: tables } = await supabase
        .from('crossbet_tables')
        .select('*')
        .in('id', tableIds)
        .in('status', ['settled', 'cancelled'])
      tableEntries = (tables || []).map(t => {
        const myPlayer = (myTablePlayers || []).find(p => p.table_id === t.id)
        const iWon = t.winner_id === session.user.id
        return { ...t, _type: 'table', wager_amount: myPlayer?.total_paid || 0, _iWon: iWon }
      })
    }

    // Load all 1v1 bets
    const { data: ovo } = await supabase
      .from('one_v_one')
      .select('*')
      .or(`created_by.eq.${session.user.id},opponent_id.eq.${session.user.id}`)
      .order('created_at', { ascending: false })

    // Load all betting lines
    const { data: lines } = await supabase
      .from('betting_lines')
      .select('*')
      .order('created_at', { ascending: false })

    const myLines = (lines || []).filter(l =>
      (l.player_ids || []).includes(session.user.id)
    )

    // Combine into unified entries
    const allEntries = [
      ...(ovo || []).map(b => ({ ...b, _type: '1v1' })),
      ...myLines.map(l => ({ ...l, _type: 'match' })),
      ...tableEntries,
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    setEntries(allEntries)

    // Load profiles
    const ids = new Set()
    ovo?.forEach(b => { ids.add(b.created_by); ids.add(b.opponent_id) })
    myLines?.forEach(l => (l.player_ids || []).forEach(id => ids.add(id)))
    if (ids.size > 0) {
      const { data: prof } = await supabase.from('profiles').select('id, first_name, last_name, username').in('id', [...ids])
      const pmap = {}
      prof?.forEach(p => { pmap[p.id] = p })
      setProfiles(pmap)
    }

    // Load matches for betting lines
    const matchIds = [...new Set(myLines.map(l => l.match_id).filter(Boolean))]
    if (matchIds.length > 0) {
      const { data: m } = await supabase.from('matches').select('id, name, course_name').in('id', matchIds)
      const mmap = {}
      m?.forEach(m => { mmap[m.id] = m })
      setMatches(mmap)
    }

    setLoading(false)
  }

  const fmt = (dt) => dt ? new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'

  const getName = (id) => {
    const p = profiles[id]
    return p ? `${p.first_name} ${p.last_name || ''}`.trim() : '?'
  }

  const getResult = (entry) => {
    if (entry.status !== 'settled') return null
    if (entry._type === '1v1') {
      if (entry.claimed_by) {
        // Normal flow — winner claimed
        return entry.claimed_by === session.user.id ? 'won' : 'lost'
      } else {
        // Old flow — confirmed_by means they confirmed they lost
        return entry.confirmed_by === session.user.id ? 'lost' : 'won'
      }
    }
    if (entry._type === 'table') {
      if (entry.status !== 'settled') return null
      return entry._iWon ? 'won' : 'lost'
    }
    if (entry._type === 'match') {
      const payouts = entry.payouts || {}
      const myPayout = payouts[session.user.id] || 0
      return myPayout > 0 ? 'won' : myPayout < 0 ? 'lost' : 'push'
    }
    return null
  }

  const FORMAT_LABEL = { stroke_play: 'STROKE PLAY', skins: 'SKINS', nassau: 'NASSAU' }

  const filtered = entries.filter(e => {
    if (filter === 'all') return true
    if (filter === 'active') return ['active', 'pending', 'claimed'].includes(e.status)
    if (filter === 'settled') return e.status === 'settled'
    if (filter === 'disputed') return e.status === 'disputed' || e.status === 'voided'
    return true
  })

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#7A7570', fontFamily: 'DM Sans, sans-serif' }}>Loading ledger...</div>
    </div>
  )

  const totalWon = entries.filter(e => getResult(e) === 'won').reduce((a, e) => a + parseFloat(e.wager_amount || 0), 0)
  const totalLost = entries.filter(e => getResult(e) === 'lost').reduce((a, e) => a + parseFloat(e.wager_amount || 0), 0)

  return (
    <div style={s.screen}>
      <div style={s.body}>

        {/* Summary */}
        <div style={s.summaryRow}>
          <div style={s.summaryCard}>
            <div style={s.summaryLabel}>WON</div>
            <div style={{ ...s.summaryVal, color: '#00563F' }}>${totalWon.toFixed(0)}</div>
          </div>
          <div style={s.summaryCard}>
            <div style={s.summaryLabel}>LOST</div>
            <div style={{ ...s.summaryVal, color: '#C0392B' }}>${totalLost.toFixed(0)}</div>
          </div>
          <div style={s.summaryCard}>
            <div style={s.summaryLabel}>NET</div>
            <div style={{ ...s.summaryVal, color: totalWon - totalLost >= 0 ? '#00563F' : '#C0392B' }}>
              {totalWon - totalLost >= 0 ? '+' : ''}${(totalWon - totalLost).toFixed(0)}
            </div>
          </div>
        </div>

        {/* Filter */}
        <div style={s.filterRow}>
          {['all', 'active', 'settled', 'disputed'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ ...s.filterBtn, ...(filter === f ? s.filterBtnActive : {}) }}>
              {f.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Ledger */}
        <div style={s.ledger}>
          {filtered.length === 0 ? (
            <div style={s.emptyRow}>No entries</div>
          ) : filtered.map((entry, i) => {
            const result = getResult(entry)
            const isFirst = i === 0
            return (
              <div key={entry.id} style={{ ...s.ledgerRow, ...(isFirst ? {} : { borderTop: '1px solid #EDE8D8' }) }}>
                <div style={s.ledgerLeft}>
                  <div style={s.ledgerTitle}>
                    {entry._type === 'table' ? (
                      <>🎯 TABLE · {entry.name}</>
                    ) : entry._type === '1v1' ? (
                      <>⚔️ 1V1 · {getName(entry.created_by === session.user.id ? entry.opponent_id : entry.created_by)}</>
                    ) : entry._type === 'match' ? (
                      <>{FORMAT_LABEL[entry.format] || entry.format} · {matches[entry.match_id]?.name || matches[entry.match_id]?.course_name || '—'}</>
                    ) : null}
                  </div>
                  {entry._type === '1v1' && (
                    <div style={s.ledgerDesc}>{entry.description}</div>
                  )}
                  <div style={s.ledgerDate}>{fmt(entry.created_at)}</div>
                </div>
                <div style={s.ledgerRight}>
                  <div style={{ ...s.ledgerStatus, color: STATUS_COLOR[entry.status] }}>
                    {STATUS_LABEL[entry.status]}
                  </div>
                  <div style={{ ...s.ledgerAmt, color: result === 'won' ? '#00563F' : result === 'lost' ? '#C0392B' : '#C19A6B' }}>
                    {result === 'won' ? '+' : result === 'lost' ? '-' : ''}${entry.wager_amount}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

      </div>
    </div>
  )
}

const s = {
  screen: { display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", background: '#FDF9E7' },
  body: { padding: '16px 24px 48px', display: 'flex', flexDirection: 'column', gap: 12 },
  summaryRow: { display: 'flex', gap: 8 },
  summaryCard: { flex: 1, background: '#FFFFFF', borderRadius: 16, padding: '12px 10px', border: '1px solid #E0DDD4', textAlign: 'center' },
  summaryLabel: { fontSize: 10, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5 },
  summaryVal: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, marginTop: 4 },
  filterRow: { display: 'flex', gap: 6 },
  filterBtn: { flex: 1, padding: '7px 4px', background: '#FDF9E7', border: '1.5px solid #E0DDD4', borderRadius: 10, fontSize: 10, fontWeight: 700, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.5 },
  filterBtnActive: { background: '#0A0A0A', border: '1.5px solid #0A0A0A', color: '#FDF9E7' },
  ledger: { background: '#FFFFFF', borderRadius: 20, border: '1px solid #E0DDD4', overflow: 'hidden' },
  ledgerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', gap: 10 },
  ledgerLeft: { flex: 1 },
  ledgerTitle: { fontSize: 13, fontWeight: 700, color: '#1A1814' },
  ledgerDesc: { fontSize: 11, color: '#7A7570', marginTop: 1 },
  ledgerDate: { fontSize: 10, color: '#B0ACA4', marginTop: 2 },
  ledgerRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 },
  ledgerStatus: { fontSize: 9, fontWeight: 700, letterSpacing: 1 },
  ledgerAmt: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 20 },
  emptyRow: { padding: 24, textAlign: 'center', color: '#7A7570', fontSize: 13 },
}
